import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { usePageTitle } from '../design-system/v2';
import { GRAPH_SVG } from './launch/graphSvg';
import './launch/launch.css';
import { BrandLockup } from '../components/common/BrandLockup';

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (cfg: Record<string, unknown>) => void;
          renderButton: (el: HTMLElement, cfg: Record<string, unknown>) => void;
          prompt: () => void;
        };
      };
    };
  }
}

function loadGoogleScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.google?.accounts?.id) {
      resolve();
      return;
    }
    const existing = document.getElementById('google-gsi');
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('Failed to load Google script')));
      return;
    }
    const script = document.createElement('script');
    script.id = 'google-gsi';
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google script'));
    document.head.appendChild(script);
  });
}

const CONTACT_HREF = 'mailto:ensomirai@himitsulab.com';
const SECTIONS = ['top', 'how', 'product'] as const;
type Section = (typeof SECTIONS)[number];

const Arrow = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);

/**
 * Launch page + sign-in. Shown at /login, so it is where users land when
 * starting signed-out and after logout. `#signin` switches to the sign-in card.
 */
const LoginPage: React.FC = () => {
  const { user, loading, config, loginWithDev, loginWithGoogleIdToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const navState = location.state as { from?: string; reason?: string } | null;
  const from = navState?.from || '/projects/all';
  const showSignin = location.hash === '#signin' || navState?.reason === 'session_expired';
  usePageTitle(showSignin ? 'Sign in' : 'EnsoMirai++');

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [fieldErr, setFieldErr] = useState<{ name?: boolean; email?: boolean }>({});
  const [error, setError] = useState<string | null>(
    navState?.reason === 'session_expired' ? 'Your session expired. Please sign in again.' : null,
  );
  const [busy, setBusy] = useState(false);
  const [active, setActive] = useState<Section>('top');
  const [scrolled, setScrolled] = useState(false);

  const scrollerRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLElement>(null);
  const graphRef = useRef<HTMLDivElement>(null);
  const heroRef = useRef<HTMLElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const googleBtnRef = useRef<HTMLDivElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);

  const go = useCallback(
    (view: 'signin' | 'home') => {
      // keep router state (from / reason) so post-login redirect still works
      navigate({ hash: view === 'signin' ? '#signin' : '' }, { state: location.state });
      scrollerRef.current?.scrollTo(0, 0);
    },
    [navigate, location.state],
  );

  useEffect(() => {
    if (showSignin) titleRef.current?.focus({ preventScroll: true });
  }, [showSignin]);

  // Highlight the tab for the section in view
  const updateTab = useCallback(() => {
    const el = scrollerRef.current;
    if (!el || showSignin) return;
    const y = el.scrollTop;
    setScrolled(y > 4);
    let next: Section = 'top';
    if (el.clientHeight + y >= el.scrollHeight - 4) next = 'product';
    else {
      const top = el.getBoundingClientRect().top;
      const line = top + (headerRef.current?.offsetHeight ?? 0) + el.clientHeight * 0.3;
      SECTIONS.forEach((id) => {
        const sec = document.getElementById(id);
        if (sec && sec.getBoundingClientRect().top <= line) next = id;
      });
    }
    setActive(next);
  }, [showSignin]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        updateTab();
      });
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', updateTab);
    updateTab();
    return () => {
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', updateTab);
    };
  }, [updateTab]);

  // Hero graph: gentle parallax, paused for reduced motion
  useEffect(() => {
    if (showSignin) return;
    const svg = graphRef.current?.querySelector('svg');
    const hero = heroRef.current;
    if (!svg || !hero) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      (svg as SVGSVGElement).pauseAnimations?.();
      return;
    }
    const onMove = (e: PointerEvent) => {
      const r = hero.getBoundingClientRect();
      const dx = (e.clientX - r.left) / r.width - 0.5;
      const dy = (e.clientY - r.top) / r.height - 0.5;
      svg.style.transform = `translate(${dx * -14}px, ${dy * -14}px)`;
    };
    hero.addEventListener('pointermove', onMove);
    return () => hero.removeEventListener('pointermove', onMove);
  }, [showSignin]);

  const scrollToSection = (e: React.MouseEvent, id: Section) => {
    e.preventDefault();
    if (showSignin) {
      go('home');
      requestAnimationFrame(() => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }));
      return;
    }
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (!showSignin || !config?.google_enabled || !config.google_client_id || !googleBtnRef.current) return;
    let cancelled = false;
    (async () => {
      try {
        await loadGoogleScript();
        if (cancelled || !window.google?.accounts?.id || !googleBtnRef.current) return;
        window.google.accounts.id.initialize({
          client_id: config.google_client_id,
          callback: async (response: { credential?: string }) => {
            if (!response.credential) return;
            setBusy(true);
            setError(null);
            try {
              await loginWithGoogleIdToken(response.credential);
              navigate(from, { replace: true });
            } catch (err) {
              setError(err instanceof Error ? err.message : 'Google sign-in failed');
            } finally {
              setBusy(false);
            }
          },
        });
        googleBtnRef.current.innerHTML = '';
        window.google.accounts.id.renderButton(googleBtnRef.current, {
          theme: 'filled_black',
          size: 'large',
          shape: 'pill',
          text: 'continue_with',
          width: 320,
        });
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Could not initialize Google sign-in');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showSignin, config, from, loginWithGoogleIdToken, navigate]);

  if (!loading && user) {
    return <Navigate to={from} replace />;
  }

  const onDevLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    const badName = !name.trim();
    const badEmail = !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
    setFieldErr({ name: badName, email: badEmail });
    if (badName) return nameRef.current?.focus();
    if (badEmail) return emailRef.current?.focus();
    setBusy(true);
    setError(null);
    try {
      await loginWithDev(email.trim(), name.trim());
      navigate(from, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign-in failed');
    } finally {
      setBusy(false);
    }
  };

  const devEnabled = config ? config.dev_login_enabled !== false : false;

  return (
    <div className="ens-launch" ref={scrollerRef}>
      <div className="page">
        <header className={`top${scrolled && !showSignin ? ' scrolled' : ''}`} ref={headerRef}>
          <BrandLockup href="#top" height={28} onClick={(e) => { e.preventDefault(); go('home'); }} />
          {!showSignin && (
            <nav className="tabs" aria-label="Sections">
              <a href="#top" aria-current={active === 'top' ? 'true' : undefined} onClick={(e) => scrollToSection(e, 'top')}>Home</a>
              <a href="#how" aria-current={active === 'how' ? 'true' : undefined} onClick={(e) => scrollToSection(e, 'how')}>How it works</a>
              <a href="#product" aria-current={active === 'product' ? 'true' : undefined} onClick={(e) => scrollToSection(e, 'product')}>Product</a>
            </nav>
          )}
          {!showSignin && (
            <a className="btn" href="#signin" onClick={(e) => { e.preventDefault(); go('signin'); }}>Sign in</a>
          )}
        </header>

        {!showSignin ? (
          <div id="home">
            <section className="launch" id="top" ref={heroRef}>
              <div className="copy">
                <span className="kicker">AUTONOMOUS APPLICATION MAINTENANCE</span>
                <h1>From connected systems<br />to <span>verified fixes.</span></h1>
                <p className="lede">EnsoMirai++ understands your application, investigates incidents against the real codebase, and prepares fixes your engineers approve before anything ships.</p>
                <div className="ctas">
                  <a className="btn primary lg" href="#signin" onClick={(e) => { e.preventDefault(); go('signin'); }}>Sign in <Arrow /></a>
                </div>
              </div>
              <div className="graph" aria-hidden="true" ref={graphRef} dangerouslySetInnerHTML={{ __html: GRAPH_SVG }} />
              <a className="scroll-cue" href="#how" aria-label="Scroll to how it works" onClick={(e) => scrollToSection(e, 'how')}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 5v14M6 13l6 6 6-6" /></svg>
              </a>
            </section>

            <section className="view" id="how" aria-labelledby="how-title">
              <div className="view-head">
                <span className="kicker">HOW IT WORKS</span>
                <h2 id="how-title">This is the process.</h2>
                <p className="lede">Six steps from connecting your application to a verified, human-approved fix.</p>
              </div>
              <ol className="steps six">
                <li><span className="stp"><em>1</em></span><b>Ingest the repository</b><span>Connect the app’s code so EnsoMirai++ can inventory and understand it.</span></li>
                <li><span className="stp"><em>2</em></span><b>Connect operational signals</b><span>Link the ops inbox, upload or send runtime logs, and bring in alerts or other monitoring inputs.</span></li>
                <li><span className="stp"><em>3</em></span><b>Monitor health</b><span>See application health, alerts, incidents, errors, and jobs in one place.</span></li>
                <li><span className="stp"><em>4</em></span><b>Investigate incidents</b><span>When something fails, EnsoMirai++ investigates against the repo and the evidence.</span></li>
                <li className="you"><span className="stp"><em>5</em>you decide</span><b>Decide ownership</b><span>Confirm who owns the resolution: Operator, Engineering, Configuration, and so on.</span></li>
                <li className="you"><span className="stp"><em>6</em>you approve</span><b>Approve and verify the fix</b><span>Human approval, then the change is applied and validated.</span></li>
              </ol>
              <div className="ctas">
                <a className="btn primary" href="#signin" onClick={(e) => { e.preventDefault(); go('signin'); }}>Sign in to get started <Arrow /></a>
              </div>
            </section>

            <section className="view" id="product" aria-labelledby="pf-title">
              <div className="view-head">
                <span className="kicker">PRODUCT</span>
                <h2 id="pf-title">What you get in EnsoMirai++</h2>
                <p className="lede">These are the capabilities inside that process.</p>
              </div>
              <ul className="areas caps">
                <li><em>01</em><b>Repository intelligence</b><span>Ingest and inventory the application: services, APIs, components and architecture context.</span></li>
                <li><em>02</em><b>Operations connections</b><span>Connect the inbox, runtime logs, alerts and related signals so incidents have real evidence.</span></li>
                <li><em>03</em><b>Monitoring console</b><span>One view of application health, alerts, open incidents, error trends and scheduled jobs.</span></li>
                <li><em>04</em><b>Incident operations</b><span>Triage and work incidents in a structured Operate workflow.</span></li>
                <li><em>05</em><b>RCA &amp; ownership</b><span>Evidence-backed root-cause reasoning, with clear ownership decisions when the cause or owner is unresolved.</span></li>
                <li><em>06</em><b>Engineering Intelligence</b><span>Autonomous investigation toward a remediation package — with humans approving what ships.</span></li>
                <li><em>07</em><b>Governance &amp; approval</b><span>Keep change control in the loop: review, approve, apply, verify.</span></li>
              </ul>
            </section>
          </div>
        ) : (
          <main className="si" id="signin">
            <div className="si-card">
              <div>
                <h1 id="si-title" tabIndex={-1} ref={titleRef}>Sign in</h1>
                <p className="sub">Continue to your EnsoMirai++ workspace.</p>
              </div>

              {error && <p className="alert" role="alert">{error}</p>}

              {!config ? (
                <p className="sub" role="status">Checking sign-in options…</p>
              ) : (
                <>
                  {config.google_enabled && <div className="sso-host" ref={googleBtnRef} />}
                  {config.google_enabled && devEnabled && <div className="divider">or with email</div>}
                  {devEnabled && (
                    <form onSubmit={onDevLogin} noValidate>
                      <label className="fld">Display name
                        <input ref={nameRef} type="text" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" placeholder="Your name" required aria-invalid={fieldErr.name ? 'true' : 'false'} aria-describedby="name-err" />
                      </label>
                      {fieldErr.name && <p className="err" id="name-err">Enter your name.</p>}
                      <label className="fld">Work email
                        <input ref={emailRef} type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" placeholder="you@company.com" required aria-invalid={fieldErr.email ? 'true' : 'false'} aria-describedby="email-err" />
                      </label>
                      {fieldErr.email && <p className="err" id="email-err">Enter a valid work email.</p>}
                      <button className="btn primary" type="submit" disabled={busy || loading} aria-busy={busy}>Sign in</button>
                      {busy && <p className="ok-msg" role="status">Signing you in…</p>}
                    </form>
                  )}
                </>
              )}
              <p className="si-alt">No account yet? <a href={CONTACT_HREF}>Request access</a></p>
            </div>
          </main>
        )}

        <footer className="bottom">
          <span>© 2026 EnsoMirai++</span>
          <span className="grow" />
          <a href="#privacy">Privacy</a>
          <a href="#terms">Terms</a>
        </footer>
      </div>
    </div>
  );
};

export default LoginPage;
